import React from 'react';
import {
  Badge,
  CondensedHeader,
  CondensedLabel,
  CondensedMedium,
  Gutter,
  Layout,
  LayoutItem,
  ImageCircle,
  CandidateProfileUtil,
  Flashy,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';
import type { AhqSubmission, OpportunityCandidate } from '@axiom/validation';

import { CandidateRateUtil } from '../../utils/candidate-rate-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { OpportunityTalentRateIndicator } from '../OpportunityTalentRateIndicator/OpportunityTalentRateIndicator';

import { OpportunitySubmissionUtil } from './opportunity-submission-util';
import { OpportunitySubmissionCandidateRateUtil } from './opportunity-submission-candidate-rate-util';

const { AllStatusLabels } = CandidateOpportunitiesConst;

export type OpportunitySubmissionCandidateListItemProps = {
  candidate: OpportunityCandidate;
  submission: AhqSubmission;
};

export const OpportunitySubmissionCandidateListItem = ({
  candidate,
  submission,
}: OpportunitySubmissionCandidateListItemProps) => {
  const yearsRange = OpportunitySubmissionUtil.getYearsOfExperienceRange(
    candidate.yearsOfExperience
  );

  return (
    <div data-test="CANDIDATE_LIST_ITEM">
      <Layout>
        <Layout position="middle">
          <LayoutItem rightGutter="12px">
            <ImageCircle
              name="CANDIDATE_IMAGE"
              size="small"
              src={CandidateProfileUtil.getProfileImageUri(
                { profileImageKey: candidate.profileImageKey ?? null },
                'W_56'
              )}
              imageName={candidate.calculatedDisplayName}
            />
          </LayoutItem>
          <LayoutItem stretched>
            <CondensedHeader name="CANDIDATE_NAME" maxLines={1}>
              {candidate.calculatedDisplayName}
            </CondensedHeader>
            <Gutter bottom="16px" />
            <Layout position="middle">
              <LayoutItem rightGutter="8px">
                <Layout direction="vertical">
                  <CondensedLabel>
                    <Flashy bold>EXP</Flashy>
                  </CondensedLabel>
                  {OpportunitySubmissionCandidateRateUtil.formatDisplayRate({
                    opportunityCandidate: candidate,
                    submission,
                  }) && (
                    <OpportunityTalentRateIndicator
                      opportunityCandidate={candidate}
                    />
                  )}
                </Layout>
              </LayoutItem>
              <LayoutItem stretched>
                <Layout direction="vertical">
                  <CondensedMedium name="CANDIDATE_EXPERIENCE">
                    {yearsRange ? `${yearsRange} years` : '--'}
                  </CondensedMedium>
                  {CandidateRateUtil.hasSubmittedRate(candidate) ? (
                    <CondensedMedium name="CANDIDATE_RATE">
                      {OpportunitySubmissionCandidateRateUtil.formatDisplayRate(
                        { opportunityCandidate: candidate, submission }
                      )}
                    </CondensedMedium>
                  ) : (
                    <CondensedMedium name="CANDIDATE_RATE">
                      Rate Not Set
                    </CondensedMedium>
                  )}
                </Layout>
              </LayoutItem>
            </Layout>
          </LayoutItem>
        </Layout>
        {candidate.candidateStatus ? (
          <Layout position="top right" name="candidate-status-badge">
            <Badge
              background={CandidateUtil.getCandidateStatusBadgeColor(
                candidate.candidateStatus
              )}
              name={`${candidate.id}-candidate-status-badge`}
            >
              {
                AllStatusLabels[
                  candidate.candidateStatus as keyof typeof AllStatusLabels
                ]
              }
            </Badge>
          </Layout>
        ) : null}
      </Layout>
    </div>
  );
};
